import React, { useState, useEffect } from "react";
import Carousel from "../../Carousel/Carousel";
import styles from "./AddressForm.module.css";
import { getCustomerAddresses, createAddress } from "../../api";

const AddressForm = ({ onSubmit, customerId }) => {
  const [formData, setFormData] = useState({
    address_line: "",
    area: "",
    landmark: "",
    pincode: "",
    city: "",
    state: "",
    country: "UAE",
    is_default: false,
    address_type: "Home",
  });

  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    getCustomerAddresses()
      .then((response) => {
        setAddresses(response.data);
        const def = response.data.find((a) => a.is_default === 1);
        if (def) setSelectedAddressId(def.address_id);
      })
      .catch((err) => console.error("Error fetching addresses:", err));
  }, []);

  const handleSelectAddress = (id) => {
    setSelectedAddressId(id);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const requestData = {
      ...formData,
      is_default: formData.is_default ? 1 : 0,
    };

    try {
      const response = await createAddress(requestData, customerId);
      onSubmit(response.data);
      setAddresses((prev) => [...prev, response.data]);
      setShowAddressForm(false);
      setSelectedAddressId(null);
    } catch (error) {
      setError(error.message || "Failed to save address.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.addressContainer}>
      <h2 className={styles.title}>
        {showAddressForm ? "Add a New Address" : "Select Delivery Address"}
      </h2>

      {!showAddressForm && addresses.length > 0 && (
        <Carousel
          addresses={addresses}
          selectedAddressId={selectedAddressId}
          onSelect={handleSelectAddress}
        />
      )}

      {!showAddressForm && (
        <div className={styles.buttonGroup}>
          {addresses.length > 0 && (
            <button
              type="button"
              className={styles.selectButton}
              onClick={() =>
                onSubmit(
                  addresses.find(
                    (address) => address.address_id === selectedAddressId
                  )
                )
              }
              disabled={!selectedAddressId}
            >
              Select Address
            </button>
          )}
          <button
            type="button"
            className={styles.addNewButton}
            onClick={() => {
              setShowAddressForm(true);
              setSelectedAddressId(null);
            }}
          >
            + Add New Address
          </button>
        </div>
      )}

      {showAddressForm && (
        <form
          onSubmit={handleSubmit}
          className={`${styles.addressForm} ${styles.gridForm}`}
        >
          {error && <div className={styles.errorMessage}>{error}</div>}

          <div className={styles.col}>
            <label>Flat, House no., Building, Company, Apartment</label>
            <input
              type="text"
              name="address_line"
              value={formData.address_line}
              onChange={handleChange}
              className={styles.input}
              required
            />
          </div>

          <div className={styles.col}>
            <label>Area, Street, Sector</label>
            <input
              type="text"
              name="area"
              value={formData.area}
              onChange={handleChange}
              className={styles.input}
              required
            />
          </div>

          <div className={styles.col}>
            <label>Landmark</label>
            <input
              type="text"
              name="landmark"
              value={formData.landmark}
              onChange={handleChange}
              className={styles.input}
              placeholder="E.g. near Dubai Mall"
            />
          </div>

          <div className={styles.col}>
            <label>Pincode</label>
            <input
              type="text"
              name="pincode"
              value={formData.pincode}
              onChange={handleChange}
              className={styles.input}
            />
          </div>

          <div className={styles.col}>
            <label>Town/City</label>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              className={styles.input}
              required
            />
          </div>

          <div className={styles.col}>
            <label>State</label>
            <select
              name="state"
              value={formData.state}
              onChange={handleChange}
              className={styles.input}
              required
            >
              <option value="">Select</option>
              <option value="Dubai">Dubai</option>
              <option value="Abu Dhabi">Abu Dhabi</option>
              <option value="Sharjah">Sharjah</option>
              <option value="Umm Al Qaiwain">Umm Al Qaiwain</option>
              <option value="Fujairah">Fujairah</option>
              <option value="Ajman">Ajman</option>
              <option value="Ras Al Khaimah">Ras Al Khaimah</option>
            </select>
          </div>

          <div className={styles.col}>
            <label>Country/Region</label>
            <select
              name="country"
              value={formData.country}
              onChange={handleChange}
              className={styles.input}
            >
              <option value="UAE">UAE</option>
            </select>
          </div>

          <div className={styles.col}>
            <label>Address Type</label>
            <select
              name="address_type"
              value={formData.address_type}
              onChange={handleChange}
              className={styles.input}
            >
              <option value="Home">Home</option>
              <option value="Office">Office</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div className={styles.formGroup}>
            <label>
              <input
                type="checkbox"
                name="is_default"
                checked={formData.is_default}
                onChange={handleChange}
              />{" "}
              Make this my default address
            </label>
          </div>

          <div className={styles.buttonGroup}>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Add Address"}
            </button>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={() => setShowAddressForm(false)}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default AddressForm;
